import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  IconButton,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { AppColors } from '../theme/colors';
import { useIsDesktop } from '../hooks/useMediaQuery';

/**
 * 利用規約の本文。
 *
 * 原本は レフリアメモ/利用規約の改定案.md（第3版・全15条）。
 * 条文を足し引きするときは原本とこの配列の両方を直す。
 */
const TERMS_LAST_UPDATED = '2026年8月28日';

const TERMS_SECTIONS: { title: string; body: string }[] = [
  {
    title: '第1条（目的）',
    body: '本利用規約は、レフリア株式会社（以下「当社」）が提供するLiaVoice（以下「本サービス」）の利用条件を定めるものです。利用者は、本サービスを利用することにより、本利用規約に同意したものとみなします。',
  },
  {
    title: '第2条（定義）',
    body: [
      '本利用規約において使用する用語の意義は、次の各号に定めるところによります。',
      '',
      '1. 「本サービス」とは、職場の風土改善のための匿名意見投稿プラットフォームをいいます。',
      '2. 「導入組織」とは、本サービスの利用を申し込み、当社の承認を受けた法人その他の組織をいいます。',
      '3. 「投稿者」とは、導入組織から案内された方法により本サービスを利用し、投稿を行う個人をいいます。',
      '4. 「利用者」とは、導入組織および投稿者を総称していいます。',
      '5. 「投稿」とは、利用者が本サービスを通じて投稿する意見、感想、その他の情報をいいます。',
    ].join('\n'),
  },
  {
    title: '第3条（利用規約の変更）',
    body: '当社は、必要と判断した場合には、利用者に通知することなくいつでも本利用規約を変更することができるものとします。なお、本利用規約の変更後、本サービスの利用を開始した場合には、当該利用者は変更後の利用規約に同意したものとみなします。',
  },
  {
    title: '第4条（利用登録）',
    body: [
      '本サービスを導入する法人その他の組織は、本利用規約に同意の上、当社の定める方法により利用登録を申請し、当社がこれを承認することによって、利用登録が完了するものとします。',
      '',
      '投稿者は、導入組織から案内された方法により本サービスにアクセスするものとし、投稿者個人による利用登録を必要としません。',
      '',
      '当社は、利用登録の申請者に以下の事由があると判断した場合、利用登録の申請を承認しないことがあり、その理由については一切の開示義務を負わないものとします。',
      '',
      '1. 利用登録の申請に際して虚偽の事項を届け出た場合',
      '2. 本利用規約に違反したことがある者からの申請である場合',
      '3. その他、当社が利用登録を相当でないと判断した場合',
    ].join('\n'),
  },
  {
    title: '第5条（匿名性について）',
    body: [
      '本サービスは、投稿者個人を特定できない設計となっています。具体的には以下の通りです。',
      '',
      '1. 投稿には、投稿者の氏名、メールアドレス、社員番号その他の個人を特定する情報を紐づけません',
      '2. システムの安定運用および不正アクセスの防止のため、当社が利用するクラウド基盤の通信ログに接続元のIPアドレスが一定期間記録されますが、当社は投稿内容とIPアドレスを紐づけて管理することはありません',
      '3. 通信はSSL/TLSにより暗号化して保護します',
      '',
      '当社が取得する情報の詳細は、当社「プライバシーポリシー」をご確認ください。',
    ].join('\n'),
  },
  {
    title: '第6条（禁止事項）',
    body: [
      '利用者は、本サービスの利用にあたり、以下の行為をしてはなりません。',
      '',
      '1. 法令または公序良俗に違反する行為',
      '2. 犯罪行為に関連する行為',
      '3. 本サービスの内容等、本サービスに含まれる著作権、商標権ほか知的財産権を侵害する行為',
      '4. 当社、ほかの利用者、またはその他第三者のサーバーまたはネットワークの機能を破壊したり、妨害したりする行為',
      '5. 当社のサービスの運営を妨害するおそれのある行為',
      '6. 不正アクセスをし、またはこれを試みる行為',
      '7. その他、当社が不適切と判断する行為',
    ].join('\n'),
  },
  {
    title: '第7条（本サービスの提供の停止等）',
    body: [
      '当社は、以下のいずれかの事由があると判断した場合、利用者に事前に通知することなく本サービスの全部または一部の提供を停止または中断することができるものとします。',
      '',
      '1. 本サービスにかかるコンピュータシステムの保守点検または更新を行う場合',
      '2. 地震、落雷、火災、停電または天災などの不可抗力により、本サービスの提供が困難となった場合',
      '3. コンピュータまたは通信回線等が事故により停止した場合',
      '4. その他、当社が本サービスの提供が困難と判断した場合',
      '',
      '当社は、本サービスの提供の停止または中断により、利用者または第三者が被ったいかなる不利益または損害についても、一切の責任を負わないものとします。',
    ].join('\n'),
  },
  {
    title: '第8条（利用制限および登録抹消）',
    body: [
      '当社は、導入組織が以下のいずれかに該当する場合には、事前の通知なく、投稿データを削除し、または当該導入組織に対して本サービスの全部もしくは一部の利用を制限しまたは利用者としての登録を抹消することができるものとします。',
      '',
      '1. 本利用規約のいずれかの条項に違反した場合',
      '2. 登録事項に虚偽の事実があることが判明した場合',
      '3. 決済手段として当該導入組織が届け出たクレジットカードが利用停止となった場合',
      '4. 料金等の支払債務の不履行があった場合',
      '5. 当社からの連絡に対し、一定期間返答がない場合',
      '6. 本サービスについて、最終の利用から一定期間利用がない場合',
      '7. その他、当社が本サービスの利用を適当でないと判断した場合',
      '',
      '当社は、本条に基づき当社が行った行為により利用者に生じた損害について、一切の責任を負いません。',
    ].join('\n'),
  },
  {
    title: '第9条（退会）',
    body: [
      '導入組織は、当社の定める退会手続により、本サービスから退会できるものとします。',
      '',
      '退会にあたり、当社の事務処理に遅滞が生じない場合、当社は、当社の定める方法により導入組織に退会の完了を通知します。',
    ].join('\n'),
  },
  {
    title: '第10条（保証の否認および免責事項）',
    body: [
      '当社は、本サービスに事実上または法律上の瑕疵（安全性、信頼性、正確性、完全性、有効性、特定の目的への適合性、セキュリティなどに関する欠陥、エラーやバグ、権利侵害などを含みます。）がないことを明示的にも黙示的にも保証しておりません。',
      '',
      '当社は、本サービスに起因して利用者に生じたあらゆる損害について一切の責任を負いません。ただし、本サービスに関する当社と利用者との間の契約（本利用規約を含みます。）が消費者契約法に定める消費者契約となる場合、この免責規定は適用されません。',
      '',
      '前項ただし書に定める場合であっても、当社は、当社の過失（重過失を除きます。）による債務不履行または不法行為により利用者に生じた損害のうち特別な事情から生じた損害（当社または利用者が損害発生につき予見し、または予見し得た場合を含みます。）について一切の責任を負いません。また、当社の過失（重過失を除きます。）による債務不履行または不法行為により利用者に生じた損害の賠償は、導入組織から当該損害が発生した月に受領した利用料の額を上限とします。',
    ].join('\n'),
  },
  {
    title: '第11条（サービス内容の変更等）',
    body: '当社は、利用者に通知することなく、本サービスの内容を変更しまたは本サービスの提供を中止することができるものとし、これによって利用者に生じた損害について一切の責任を負いません。',
  },
  {
    title: '第12条（個人情報の取扱い）',
    body: [
      '当社は、本サービスの利用によって取得する情報については、当社「プライバシーポリシー」に従い適切に取り扱うものとします。',
      '',
      '投稿については、投稿者の氏名、メールアドレス、社員番号その他投稿者を直接特定する情報を投稿内容に紐づけて収集しません。接続元IPアドレスその他の技術情報を含む取得・記録情報の詳細は、「プライバシーポリシー」をご確認ください。',
      '',
      '契約および請求のために必要な範囲で、導入組織のご担当者情報をお預かりします。',
    ].join('\n'),
  },
  {
    title: '第13条（通知または連絡）',
    body: '利用者と当社との間の通知または連絡は、当社の定める方法によって行うものとします。当社は、導入組織から、当社が別途定める方式に従った変更届け出がない限り、現在登録されている連絡先が有効なものとみなして当該連絡先へ通知または連絡を行い、これらは、発信時に導入組織へ到達したものとみなします。',
  },
  {
    title: '第14条（権利義務の譲渡の禁止）',
    body: '導入組織は、当社の書面による事前の承諾なく、利用契約上の地位または本利用規約に基づく権利もしくは義務を第三者に譲渡し、または担保に供することはできません。',
  },
  {
    title: '第15条（準拠法・裁判管轄）',
    body: [
      '本利用規約の解釈にあたっては、日本法を準拠法とします。',
      '',
      '本サービスに関して紛争が生じた場合には、当社の本店所在地を管轄する裁判所を専属的合意管轄とします。',
    ].join('\n'),
  },
];

export const TermsOfServicePage: React.FC = () => {
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: AppColors.surface }}>
      {/* ヘッダー */}
      <AppBar
        position="static"
        sx={{
          bgcolor: 'white',
          boxShadow: 'none',
          borderBottom: '1px solid #e0e0e0',
        }}
      >
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate(-1)}
            sx={{ color: 'black' }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography
            variant="h6"
            component="div"
            sx={{
              flexGrow: 1,
              color: 'black',
              fontWeight: 600,
            }}
          >
            利用規約
          </Typography>
        </Toolbar>
      </AppBar>

      {/* メインコンテンツ */}
      <Box sx={{
        p: 2,
        maxWidth: isDesktop ? '800px' : '100%',
        mx: isDesktop ? 'auto' : 0,
      }}>
        {/* ヘッダーセクション */}
        <Box sx={{ mb: 3 }}>
          <Card
            sx={{
              bgcolor: `${AppColors.primary}20`,
              border: `1px solid ${AppColors.primary}50`,
              borderRadius: 2,
            }}
          >
            <CardContent sx={{ p: 2.5, textAlign: 'center' }}>
              <Typography
                sx={{
                  fontSize: 20,
                  fontWeight: 'bold',
                  mb: 1,
                }}
              >
                利用規約
              </Typography>
              <Typography
                sx={{
                  fontSize: 14,
                  color: AppColors.textSecondary,
                  mb: 1.5,
                }}
              >
                レフリア株式会社（以下「当社」）が提供するLiaVoice（以下「本サービス」）のご利用にあたって適用される条件を定めるものです。
              </Typography>
              <Typography
                sx={{
                  fontSize: 12,
                  color: AppColors.textSecondary,
                }}
              >
                最終更新日: {TERMS_LAST_UPDATED}
              </Typography>
            </CardContent>
          </Card>
        </Box>

        {/* 利用規約コンテンツ */}
        <Card
          sx={{
            boxShadow: 2,
            border: `1px solid ${AppColors.border}`,
            borderRadius: 2,
          }}
        >
          <CardContent sx={{ p: 2.5 }}>
            {TERMS_SECTIONS.map((section) => (
              <Box key={section.title} sx={{ mb: 2.5 }}>
                <Typography
                  sx={{
                    fontSize: 16,
                    fontWeight: 'bold',
                    color: AppColors.primary,
                    mb: 1,
                  }}
                >
                  {section.title}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 14,
                    lineHeight: 1.6,
                    color: AppColors.textPrimary,
                    whiteSpace: 'pre-line',
                  }}
                >
                  {section.body}
                </Typography>
              </Box>
            ))}
          </CardContent>
        </Card>

        {/* フッター */}
        <Box sx={{ mt: 3 }}>
          <Card
            sx={{
              bgcolor: '#f5f5f5',
              borderRadius: 2,
            }}
          >
            <CardContent sx={{ p: 2, textAlign: 'center' }}>
              <Typography
                sx={{
                  fontSize: 12,
                  color: AppColors.textSecondary,
                  mb: 1,
                }}
              >
                © 2026 レフリア株式会社
              </Typography>
              <Typography
                sx={{
                  fontSize: 12,
                  color: AppColors.textSecondary,
                }}
              >
                本利用規約は予告なく変更される場合があります。改定後の内容は本ページに掲示した時点で効力を生じます。
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
};
